// ============================================================
// web/src/lib/session.jsx — l'état d'authentification.
// ------------------------------------------------------------
// Le jeton n'est JAMAIS ici : il vit dans un cookie HttpOnly que le
// JavaScript ne peut pas lire. Ce contexte ne porte que l'identité
// renvoyée par /api/auth/me.
//
// Sur expiration de session, l'application bascule sur l'écran de
// connexion SANS recharger la page : le travail en cours reste en
// mémoire. Chez AGC, un `location.reload()` sur 401 faisait perdre le
// devis en cours de saisie.
// ============================================================

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, AUTH_EXPIRED_EVENT, setCsrfToken, clearCsrfToken } from './api.js';

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [user, setUser] = useState(null);
  // `checking` couvre la vérification initiale du cookie. Sans lui,
  // l'écran de connexion clignote une fraction de seconde à chaque
  // rechargement alors que la session est valide.
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me, csrf_token } = await api.get('/auth/me');
      // Récupéré après un rechargement de page : le front ne peut pas
      // relire le cookie, posé sur un autre domaine que le sien.
      setCsrfToken(csrf_token);
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = () => {
      clearCsrfToken();
      setUser(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = useCallback(async (username, password) => {
    const { user: me, csrf_token } = await api.post('/auth/login', { username, password });
    setCsrfToken(csrf_token);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Même si l'appel échoue, on ferme la session côté client : rester
      // sur un écran connecté après un clic sur « Se déconnecter » est
      // pire qu'une erreur réseau silencieuse.
      clearCsrfToken();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user, checking, login, logout, refresh, isAdmin: user?.role === 'admin',
      // Valider une règle proposée (assistant, étape 2) : une permission, pas le rôle.
      peutValiderRegles: user?.peut_valider_regles === true,
    }),
    [user, checking, login, logout, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession doit être utilisé dans un SessionProvider.');
  return ctx;
}
